import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  QueryConstraint,
  QueryDocumentSnapshot,
  DocumentData,
  getCountFromServer,
  writeBatch,
} from 'firebase/firestore';
import { ref, deleteObject, getMetadata } from 'firebase/storage';
import { registeredHipaaBasePath } from '../../functions/src/modules/hipaa/hipaaAssembly.shared';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '@/config/firebase';
import { Client, ClientInput, CaseStatus, Document, DocumentStatus, DocumentType } from '@/types';
import { maskSSN, maskDOB, formatSSN, isValidMaskedSSN } from '@/utils/encryption';
import { getCurrentTimestamp, parseDateString } from '@/utils/dates';
import { getClientDisplayName } from '@/utils/nameHelpers';
import { logAudit } from './auditLogService';
import { activateBilling, updateSubscriptionQuantity } from './billingService';
import { getFirm } from './firmService';
import { notify } from '@/utils/notifications';
import { logger } from '../utils/logger';
import { withErrorHandling } from '@/utils/errorHandling';
import { canCreateClient, hasUsableClientCreationCapacity } from '@/utils/paymentMethodStatus';

/**
 * Thrown when client creation is attempted on a firm that has no payment method on file.
 * Surfaced to the UI as a friendly toast / dialog rather than a permission error.
 */
export class PaymentMethodRequiredError extends Error {
  constructor(message = 'A payment method is required to create clients.') {
    super(message);
    this.name = 'PaymentMethodRequiredError';
  }
}
import { HipaaClassification } from './ocrService';
import { clientSignatureToDocSignature } from './hipaaSignatureState';
import { uploadHipaaSourceFile } from './hipaaSourceUpload';
import { sha256Hex } from '@/utils/fileHash';
import { uploadAdditionalHipaa } from './hipaaDocumentService';
import { PaginatedResult, executePaginatedQuery } from '@/utils/pagination';
import { retryWithTokenRefresh } from '@/utils/firebaseAuth';
import { getErrorMessage } from '@/utils/errorUtils';
import { nameSimilarity, ClientIdentityProjection, type IdentityMatchCategory } from '@/utils/nameMatching';

type CreateClientStage =
  | 'encrypt'
  | 'prepareHipaa'
  | 'writeClientBundle'
  | 'postCommitAudit'
  | 'postCommitBilling';

const CREATE_CLIENT_PERMISSION_MESSAGE =
  'Your session is missing firm permissions. We refreshed your access and still could not create the client. Please sign out and back in, then try again.';

const CREATE_CLIENT_PAYMENT_METHOD_MESSAGE =
  'To create clients, your firm needs a payment method on file or an active promo. Please open the Billing page to add a card, or contact support if you believe a promo should apply.';

function isPermissionDeniedError(error: unknown): boolean {
  const err = error as { code?: string };
  const message = getErrorMessage(error).toLowerCase();
  return err?.code === 'permission-denied' ||
    message.includes('missing or insufficient permissions') ||
    message.includes('permission denied') ||
    message.includes('permission_denied');
}

function logCreateClientStageError(
  stage: CreateClientStage,
  error: unknown,
  context: Record<string, unknown> = {}
): void {
  const logError = error instanceof Error ? error : new Error(String(error));
  logger.error(`createClient:${stage}`, logError, context);
}

/**
 * Get Cloud Functions callables (lazy initialization)
 */
function getEncryptionFunctions() {
  return {
    encryptFn: httpsCallable<{ value: string }, { encrypted: string }>(
      functions,
      'encryptClientData'
    ),
    batchEncryptFn: httpsCallable<{ values: string[] }, { encrypted: string[] }>(
      functions,
      'batchEncryptClientData'
    ),
  };
}

/**
 * Encrypt a single value using Cloud Function
 * @internal - Currently unused, kept for future use
 */
export async function encryptValue(value: string): Promise<string> {
  return withErrorHandling(async () => {
    const { encryptFn } = getEncryptionFunctions();
    const result = await encryptFn({ value });
    return result.data.encrypted;
  }, 'encrypt sensitive data') as Promise<string>;
}

/**
 * Encrypt multiple values using Cloud Function (more efficient)
 * Chunks values into batches of 10 (cloud function limit) and processes sequentially
 */
async function encryptValues(values: string[]): Promise<string[]> {
  return withErrorHandling(async () => {
    const BATCH_LIMIT = 10; // Cloud function enforces maximum 10 values per batch
    const { batchEncryptFn } = getEncryptionFunctions();
    const encryptedResults: string[] = [];

    // Process values in chunks of 10
    for (let i = 0; i < values.length; i += BATCH_LIMIT) {
      const chunk = values.slice(i, i + BATCH_LIMIT);

      const result = await batchEncryptFn({ values: chunk });
      if (result.data?.encrypted && Array.isArray(result.data.encrypted)) {
        encryptedResults.push(...result.data.encrypted);
      } else {
        throw new Error(`Encryption returned invalid format for chunk ${Math.floor(i / BATCH_LIMIT) + 1}`);
      }
    }

    // Verify we got the expected number of encrypted values
    if (encryptedResults.length !== values.length) {
      throw new Error(
        `Encryption mismatch: expected ${values.length} encrypted values, got ${encryptedResults.length}`
      );
    }

    return encryptedResults;
  }, 'encrypt sensitive data') as Promise<string[]>;
}

/**
 * Create a new client
 */
export async function createClient(
  firmId: string,
  userId: string,
  clientData: ClientInput,
  ocrMetadata?: {
    classification?: HipaaClassification;
    matchedProvider?: { id: string; name: string } | null;
    userConfirmedType?: boolean;
    markedAsGeneralDespiteProvider?: boolean;
    requiresAuditReview?: boolean;
  },
  hipaaMetadata?: {
    url?: string;
    storagePath?: string;
    size?: number;
  }
): Promise<string> {
  return withErrorHandling(async () => {
    // Pre-flight: block client creation when the firm has no valid payment method.
    // This is a UX boundary; Firestore rules are the security boundary.
    const firmForGate = (await getFirm(firmId)) ?? null;
    if (!canCreateClient(firmForGate)) {
      throw new PaymentMethodRequiredError();
    }

    const clientRef = doc(collection(db, `firms/${firmId}/clients`));
    const clientId = clientRef.id;
    let stage: CreateClientStage = 'encrypt';
    let clientName = `${clientData.firstName.trim()} ${clientData.lastName.trim()}`.trim();

    try {
      stage = 'encrypt';
      // V1: signed HIPAA forms sometimes mask the first 5 SSN digits ("***-**-3948").
      // When that's all we have, skip encryption — there's nothing to encrypt — and
      // mark the record so search/match flows can degrade to last-4 matching.
      const ssnFormatted = formatSSN(clientData.ssn);
      const ssnIsMasked = isValidMaskedSSN(clientData.ssn);
      const [ssnEncrypted, dobEncrypted] = ssnIsMasked
        ? await Promise.all([Promise.resolve(''), encryptValue(clientData.dateOfBirth)])
        : await encryptValues([ssnFormatted, clientData.dateOfBirth]);

      stage = 'prepareHipaa';
      let hipaaFileSizeBytes: number | undefined;
      let hipaaStoragePath: string | undefined;
      // F-16/F-12: only an uploaded File has a real name and bytes to hash.
      let hipaaFileName = 'HIPAA Authorization.pdf';
      let hipaaFileType = 'application/pdf';
      let hipaaSha256: string | null = null;

      if (clientData.hipaaAuthorizationFile) {
        if (typeof clientData.hipaaAuthorizationFile === 'string') {
          if (clientData.hipaaAuthorizationFile.startsWith('https://')) {
            hipaaStoragePath = hipaaMetadata?.storagePath || registeredHipaaBasePath(
              { hipaaAuthorizationUrl: clientData.hipaaAuthorizationFile },
              () => storage.app.options.storageBucket || '',
            ) || undefined;
          } else {
            hipaaStoragePath = clientData.hipaaAuthorizationFile;
          }
          if (!hipaaStoragePath?.startsWith(`firms/${firmId}/`)) {
            throw new Error('The HIPAA authorization must be uploaded to this firm.');
          }
          const metadata = await getMetadata(ref(storage, hipaaStoragePath));
          hipaaFileSizeBytes = metadata.size;
        } else {
          const uploaded = await uploadHipaaSourceFile(firmId, clientId, clientData.hipaaAuthorizationFile);
          hipaaStoragePath = uploaded.storagePath;
          hipaaFileSizeBytes = uploaded.size;
          hipaaFileName = clientData.hipaaAuthorizationFile.name || hipaaFileName;
          hipaaFileType = clientData.hipaaAuthorizationFile.type || hipaaFileType;
          hipaaSha256 = await sha256Hex(clientData.hipaaAuthorizationFile);
        }
      }

      const isProviderSpecific = ocrMetadata?.classification?.isProviderSpecific || false;
      const documentType: DocumentType = isProviderSpecific
        ? 'hipaa_auth_provider'
        : 'hipaa_auth_general';
      const now = getCurrentTimestamp();
      const accidentDate = clientData.dateOfAccident
        ? parseDateString(clientData.dateOfAccident)
        : null;

      const client: Omit<Client, 'clientId'> = {
        salutation: clientData.salutation,
        firstName: clientData.firstName.trim(),
        lastName: clientData.lastName.trim(),
        dateOfBirthEncrypted: dobEncrypted,
        ssnEncrypted: ssnEncrypted,
        dateOfBirthMasked: maskDOB(clientData.dateOfBirth, true),
        ssnMasked: maskSSN(ssnFormatted),
        ...(ssnIsMasked ? { ssnIsMasked: true } : {}),
        caseStatus: 'open',
        caseOpenedDate: now,
        caseClosedDate: null,
        // Parse "MM/DD/YYYY" as a LOCAL date (parseDateString -> new Date(y, m-1, d)).
        // Parsing the ISO form via `new Date("YYYY-MM-DD")` yields UTC midnight, which
        // formatDate() then renders one calendar day earlier in UTC-negative zones (S20-1).
        ...(accidentDate ? {
          dateOfAccident: Timestamp.fromDate(accidentDate),
        } : {}),
        ...(hipaaStoragePath ? { hipaaAuthorizationStoragePath: hipaaStoragePath } : {}),
        hipaaAuthorizationUploadedAt: hipaaStoragePath ? now : null,
        hipaaAuthorizationUploadedBy: hipaaStoragePath ? userId : null,
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
        updatedBy: userId,
        firmId,
        activeProvidersCount: 0,
        ...(clientData.phone !== undefined ? { phone: clientData.phone } : {}),
        ...(clientData.cellPhone !== undefined ? { cellPhone: clientData.cellPhone } : {}),
        ...(clientData.email !== undefined ? { email: clientData.email } : {}),
        ...(clientData.preferredContactMethod !== undefined
          ? { preferredContactMethod: clientData.preferredContactMethod }
          : {}),
        // Signature is verified at upload (B5-6): persist it so the case isn't shown as
        // perpetually "Pending Signature" in ClientProvidersList. 'pending' for uncertain reads.
        ...(clientData.signatureStatus
          ? {
              signatureStatus: clientData.signatureStatus,
              ...(clientData.signatureStatus === 'signed' ? { signatureCompletedAt: now } : {}),
              // #1300: record wet vs electronic for compliance review. Only stamped on
              // a verified 'signed' read — a pending/none status has no kind to record.
              ...(clientData.signatureStatus === 'signed' && clientData.signatureKind
                ? { signatureKind: clientData.signatureKind }
                : {}),
            }
          : {}),
        // Derived HIPAA denormalizations — bootstrapped here (atomic with the doc write);
        // all POST-create maintenance goes through the single writer
        // hipaaDocumentService.syncClientHipaaDenormalizedFields (deriveClientHipaaFields).
        // These initial values match that definition for one active general doc.
        hasProviderSpecificHipaa: isProviderSpecific,
        // Only count a HIPAA form if a file was actually uploaded. In custom-template mode
        // the firm supplies a signed form at request time, so no per-client file is required.
        hipaaFormsCount: hipaaStoragePath ? 1 : 0,
      };

      clientName = getClientDisplayName(client);
      const clientPayload = { ...client, clientId };

      let hipaaDocRef: ReturnType<typeof doc> | null = null;
      let hipaaDocPayload: ({ documentId: string } & Omit<Document, 'documentId'>) | null = null;

      if (hipaaStoragePath) {
        hipaaDocRef = doc(collection(db, `firms/${firmId}/documents`));
        const hipaaDocId = hipaaDocRef.id;

        const hipaaDocData: Omit<Document, 'documentId'> = {
          firmId,
          clientId,
          clientName,
          providerId: null,
          providerName: null,
          requestLogId: null,
          fileName: hipaaFileName,
          fileType: hipaaFileType,
          fileSizeBytes: hipaaFileSizeBytes || 0,
          storagePath: hipaaStoragePath,
          documentType: documentType,
          // The DOCUMENT is authoritative for its own signature state (s36). Stamp it
          // from the same create-time signal that sets client.signatureStatus — never
          // leave it absent, or the modal's "signed" banner is unreachable (s35-1) and
          // #1242's replace keys on a missing field. client 'signed' -> doc 'signed';
          // pending/none/undefined -> doc 'unsigned' (never a confident claim).
          signatureStatus: clientSignatureToDocSignature(clientData.signatureStatus),
          documentCategory: 'Legal/Authorization',
          documentDate: now,
          receivedDate: now,
          uploadedAt: now,
          uploadedBy: userId,
          uploadedByName: 'System',
          status: 'ready' as DocumentStatus,
          processingError: null,
          notes: 'HIPAA Authorization uploaded during client creation',
          tags: [],
          pageCount: null,
          thumbnailUrl: null,
          extractedText: null,
          createdAt: now,
          updatedAt: now,
          viewedBy: [],
          viewCount: 0,
          lastViewedAt: null,
          isProtected: true,
          protectedDocumentStatus: 'active',
          replacedBy: null,
          replacedAt: null,
          replacesDocument: null,
          ...(hipaaSha256 ? { sha256: hipaaSha256 } : {}),
          providerSpecific: isProviderSpecific,
          applicableProviderId: isProviderSpecific ? ocrMetadata?.matchedProvider?.id || null : null,
          detectedProviderName: ocrMetadata?.classification?.detectedProviderName || null,
          detectedProviderConfidence: ocrMetadata?.classification?.confidence || null,
          userConfirmedType: ocrMetadata?.userConfirmedType || false,
          markedAsGeneralDespiteProvider: ocrMetadata?.markedAsGeneralDespiteProvider || false,
          requiresAuditReview: ocrMetadata?.requiresAuditReview || false,
        };

        hipaaDocPayload = {
          documentId: hipaaDocId,
          ...hipaaDocData,
        };
      }

      stage = 'writeClientBundle';
      await retryWithTokenRefresh(async () => {
        const batch = writeBatch(db);
        batch.set(clientRef, clientPayload);

        if (hipaaDocRef && hipaaDocPayload) {
          batch.set(hipaaDocRef, hipaaDocPayload);
        }

        await batch.commit();
      }, 'createClient.writeClientBundle');

      if (hipaaDocPayload) {
        logger.info(`HIPAA document record created: ${hipaaDocPayload.documentId} for client ${clientId}`);
      }

      stage = 'postCommitAudit';
      try {
        await logAudit({
          firmId,
          action: 'create_client',
          resourceType: 'client',
          resourceId: clientId,
          details: {
            caseStatus: 'open',
          },
          success: true,
        });

        if (hipaaStoragePath) {
          await logAudit({
            firmId,
            action: 'upload_hipaa',
            resourceType: 'client',
            resourceId: clientId,
            details: {
              ...(hipaaDocRef ? { documentId: hipaaDocRef.id } : {}),
              // #1300: which kind of signature the upload was accepted on. Some
              // providers ask; the audit trail is where compliance looks it up.
              ...(clientData.signatureStatus ? { signatureStatus: clientData.signatureStatus } : {}),
              ...(clientData.signatureStatus === 'signed' && clientData.signatureKind
                ? { signatureKind: clientData.signatureKind }
                : {}),
            },
            success: true,
          });
        }
      } catch (auditError) {
        logCreateClientStageError(stage, auditError, {
          firmId,
          clientId,
          clientDocPath: clientRef.path,
        });
      }

      stage = 'postCommitBilling';
      try {
        const firm = await getFirm(firmId);
        const subscription = firm?.subscription;
        // A firm can recover from `inactive` if it still has customer + PM on file in Stripe —
        // this happens after the billing.service cleanup branch resets a dead subscription.
        const hasRecoverableBillingLinks =
          !!subscription?.stripeCustomerId && !!subscription?.stripePaymentMethodId;
        const canActivateFromStatus =
          subscription?.status === 'trial' ||
          subscription?.status === 'active' ||
          (subscription?.status === 'inactive' && hasRecoverableBillingLinks);
        // Promo-only firms (free_cases) reach this point with status='active' but no
        // Stripe customer — there's nothing to activate. activateBillingV2 hard-requires
        // a customer and will 400; skip it cleanly instead of surfacing a misleading
        // "billing activation failed, contact support" toast on the very first case.
        const needsBillingActivation = !!firm &&
          !!subscription?.stripeCustomerId &&
          !subscription?.stripeSubscriptionId &&
          canActivateFromStatus;

        if (needsBillingActivation) {
          logger.info(`First case created for firm ${firmId}, activating billing...`);
          await activateBilling(firmId);
          logger.info(`Billing activated successfully for firm ${firmId}`);
          notify.success('🎉 Billing activated! You\'re now on the active plan at $10 per case per month.', {
            autoClose: 7000,
          });
        } else if (firm?.subscription?.stripeSubscriptionId) {
          updateSubscriptionQuantity(firmId).catch((syncError) => {
            logCreateClientStageError('postCommitBilling', syncError, {
              firmId,
              clientId,
              clientDocPath: clientRef.path,
            });
          });
        }
      } catch (billingError) {
        logCreateClientStageError(stage, billingError, {
          firmId,
          clientId,
          clientDocPath: clientRef.path,
        });
        notify.warning('Client created successfully, but billing activation failed. Please contact support.');

        updateSubscriptionQuantity(firmId).catch((syncError) => {
          logCreateClientStageError('postCommitBilling', syncError, {
            firmId,
            clientId,
            clientDocPath: clientRef.path,
          });
        });
      }

      return clientId;
    } catch (error) {
      logCreateClientStageError(stage, error, {
        firmId,
        clientId,
        clientName,
        clientDocPath: clientRef.path,
        hipaaDocPathPrefix: `firms/${firmId}/documents/`,
        clientPayloadKeys: [
          'clientId', 'firmId', 'salutation', 'firstName', 'lastName',
          'dateOfBirthEncrypted', 'ssnEncrypted', 'dateOfBirthMasked', 'ssnMasked',
          'caseStatus', 'caseOpenedDate', 'caseClosedDate', 'dateOfAccident',
          'hipaaAuthorizationUrl', 'hipaaAuthorizationUploadedAt', 'hipaaAuthorizationUploadedBy',
          'createdAt', 'updatedAt', 'createdBy', 'updatedBy', 'activeProvidersCount',
          'phone', 'cellPhone', 'email', 'preferredContactMethod',
          'hasProviderSpecificHipaa', 'hipaaFormsCount',
        ],
        hipaaDocumentPayloadKeys: [
          'documentId', 'firmId', 'clientId', 'clientName', 'providerId', 'providerName',
          'requestLogId', 'fileName', 'fileType', 'fileSizeBytes', 'storagePath',
          'documentType', 'documentCategory', 'providerSpecific', 'applicableProviderId',
          'detectedProviderName', 'detectedProviderConfidence', 'userConfirmedType',
          'markedAsGeneralDespiteProvider', 'requiresAuditReview', 'documentDate', 'receivedDate',
          'uploadedAt', 'createdAt', 'updatedAt', 'uploadedBy', 'uploadedByName', 'status',
          'processingError', 'notes', 'tags', 'pageCount', 'thumbnailUrl', 'extractedText',
          'viewedBy', 'viewCount', 'lastViewedAt', 'isProtected', 'protectedDocumentStatus',
          'replacedBy', 'replacedAt', 'replacesDocument',
        ],
      });

      if (stage === 'writeClientBundle' && isPermissionDeniedError(error)) {
        // Distinguish "rule rejected because firm lacks client-creation capacity"
        // from "rule rejected because the user's claims look wrong". The pre-flight
        // checks capacity against a possibly-stale cached firm; re-fetch live state
        // before deciding which error to surface.
        const liveFirm = await getFirm(firmId).catch(() => null);
        if (liveFirm && !hasUsableClientCreationCapacity(liveFirm)) {
          throw new PaymentMethodRequiredError(CREATE_CLIENT_PAYMENT_METHOD_MESSAGE);
        }
        throw new Error(CREATE_CLIENT_PERMISSION_MESSAGE);
      }

      throw error;
    }
  }, 'create client') as Promise<string>;
}

/**
 * Get a single client by ID
 */
export async function getClient(firmId: string, clientId: string): Promise<Client | null> {
  return withErrorHandling(async () => {
    const clientRef = doc(db, `firms/${firmId}/clients`, clientId);
    const clientDoc = await getDoc(clientRef);

    if (clientDoc.exists()) {
      return clientDoc.data() as Client;
    }

    return null;
  }, 'get client') as Promise<Client | null>;
}

/**
 * Get all clients for a firm
 */
export async function listClients(
  firmId: string,
  caseStatus?: CaseStatus
): Promise<Client[]> {
  return withErrorHandling(async () => {
    const clientsRef = collection(db, `firms/${firmId}/clients`);

    let q = query(clientsRef, orderBy('lastName', 'asc'), orderBy('firstName', 'asc'));

    if (caseStatus) {
      q = query(clientsRef, where('caseStatus', '==', caseStatus), orderBy('lastName', 'asc'), orderBy('firstName', 'asc'));
    }

    const querySnapshot = await getDocs(q);
    const clients: Client[] = [];

    querySnapshot.forEach((doc) => {
      clients.push(doc.data() as Client);
    });

    return clients;
  }, 'list clients') as Promise<Client[]>;
}

export interface ClientIdentityMatch {
  matchCategory?: IdentityMatchCategory;
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  matchScore: number;
}

// `nameSimilarity` / `levenshteinDistance` moved to `@/utils/nameMatching`
// so they can be shared with the live-duplicate-detection hook without
// pulling the full client service into the render tree.

/**
 * Find existing clients that likely match the provided identity.
 * Mirrors the backend ClientMatcherService strategy for the frontend-only
 * Manual Entry flow, where we don't have a server-side OCR extraction pass.
 *
 * Match rules:
 *   - First + last name fuzzy similarity >= 80%
 *   - If a DOB year is provided, the client's masked DOB year must match
 *   - Returns matches sorted by score desc; highest first
 */
export async function findClientsByIdentity(
  firmId: string,
  firstName: string,
  lastName: string,
  dateOfBirth?: string,
  options: { includeNameOnlyAdvice?: boolean } = {},
): Promise<ClientIdentityMatch[]> {
  return withErrorHandling(async () => {
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    if (!trimmedFirst || !trimmedLast) return [];

    const inputYear = dateOfBirth && dateOfBirth.length >= 4
      ? dateOfBirth.slice(-4)
      : null;

    const clientsRef = collection(db, `firms/${firmId}/clients`);
    const snapshot = await getDocs(clientsRef);

    const matches: ClientIdentityMatch[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data() as Client;
      if (!data.firstName || !data.lastName) return;

      const firstSim = nameSimilarity(trimmedFirst, data.firstName);
      const lastSim = nameSimilarity(trimmedLast, data.lastName);
      const avgSim = (firstSim + lastSim) / 2;
      if (avgSim < 80) return;

      const maskedDob = data.dateOfBirthMasked || '**/**/****';
      const candidateYear = maskedDob.slice(-4);
      const sameYear = !!inputYear && candidateYear === inputYear;
      const exactName = firstSim === 100 && lastSim === 100;
      const nameOnly = !!options.includeNameOnlyAdvice && exactName && !sameYear;
      if (inputYear && !sameYear && !nameOnly) return;
      const score = exactName && sameYear ? 100 : Math.round(avgSim);

      matches.push({
        id: docSnap.id,
        firstName: data.firstName,
        lastName: data.lastName,
        dateOfBirth: maskedDob,
        matchScore: score,
        ...(options.includeNameOnlyAdvice ? { matchCategory: nameOnly ? 'name-only' as const : exactName && sameYear ? 'name-and-year' as const : 'similar-name' as const } : {}),
      });
    });

    matches.sort((a, b) => Number(a.matchCategory === 'name-only') - Number(b.matchCategory === 'name-only') || b.matchScore - a.matchScore || a.id.localeCompare(b.id));
    return matches;
  }, 'find clients by identity') as Promise<ClientIdentityMatch[]>;
}

/**
 * Projection of every client in a firm down to identity-only fields used
 * for live duplicate detection. Returns the same fields already used in
 * fuzzy matching — no new PII is introduced to the wire.
 */
export async function listClientIdentities(
  firmId: string,
): Promise<ClientIdentityProjection[]> {
  return withErrorHandling(async () => {
    const clientsRef = collection(db, `firms/${firmId}/clients`);
    const snapshot = await getDocs(clientsRef);

    const identities: ClientIdentityProjection[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data() as Client;
      if (!data.firstName || !data.lastName) return;

      const masked = data.dateOfBirthMasked || '';
      const year = masked.length >= 4 ? masked.slice(-4) : '';
      const dobYear = /^\d{4}$/.test(year) ? year : undefined;

      identities.push({
        id: docSnap.id,
        firstName: data.firstName,
        lastName: data.lastName,
        dobYear,
      });
    });

    return identities;
  }, 'list client identities') as Promise<ClientIdentityProjection[]>;
}

/**
 * Get paginated clients for a firm
 */
export async function listClientsPaginated(
  firmId: string,
  options: {
    pageSize?: number;
    lastDoc?: QueryDocumentSnapshot<DocumentData> | null;
    caseStatus?: CaseStatus;
    includeCount?: boolean;
  } = {}
): Promise<PaginatedResult<Client>> {
  const { pageSize, lastDoc, caseStatus, includeCount } = options;

  return withErrorHandling(async () => {
    const clientsRef = collection(db, `firms/${firmId}/clients`);

    // Build base query with constraints
    let constraints: QueryConstraint[] = [orderBy('lastName', 'asc'), orderBy('firstName', 'asc')];

    if (caseStatus) {
      constraints = [where('caseStatus', '==', caseStatus), ...constraints];
    }

    const baseQuery = query(clientsRef, ...constraints);

    // Use shared pagination utility
    return executePaginatedQuery<Client>(baseQuery, {
      pageSize,
      lastDoc,
      includeCount,
    });
  }, 'list clients') as Promise<PaginatedResult<Client>>;
}

/**
 * Update a client
 */
export async function updateClient(
  firmId: string,
  clientId: string,
  userId: string,
  updates: Partial<ClientInput>
): Promise<void> {
  return withErrorHandling(async () => {
    const clientRef = doc(db, `firms/${firmId}/clients`, clientId);
    const updateData: Record<string, unknown> = {
      updatedAt: getCurrentTimestamp(),
      updatedBy: userId,
    };

    // Update basic fields
    if (updates.salutation !== undefined) {
      updateData.salutation = updates.salutation;
    }

    if (updates.firstName !== undefined) {
      updateData.firstName = updates.firstName.trim();
    }

    if (updates.lastName !== undefined) {
      updateData.lastName = updates.lastName.trim();
    }

    if (updates.dateOfAccident !== undefined) {
      const trimmed = updates.dateOfAccident.trim();
      // Parse "MM/DD/YYYY" as a LOCAL date so an edit doesn't re-shift the day in
      // UTC-negative zones (S20-1). parseDateString returns null on malformed input;
      // the edit modals validate with isValidDOB upstream so that path is unreachable.
      const parsed = trimmed ? parseDateString(trimmed) : null;
      updateData.dateOfAccident = parsed ? Timestamp.fromDate(parsed) : null;
    }

    if (updates.email !== undefined) {
      updateData.email = updates.email.trim();
    }

    if (updates.phone !== undefined) {
      updateData.phone = updates.phone.trim();
    }

    if (updates.cellPhone !== undefined) {
      updateData.cellPhone = updates.cellPhone.trim();
    }

    if (updates.preferredContactMethod !== undefined) {
      updateData.preferredContactMethod = updates.preferredContactMethod;
    }

    // Update encrypted SSN and/or DOB if changed
    const valuesToEncrypt: string[] = [];
    const fieldsToUpdate: ('ssn' | 'dob')[] = [];

    if (updates.ssn !== undefined) {
      const ssnFormatted = formatSSN(updates.ssn);
      valuesToEncrypt.push(ssnFormatted);
      fieldsToUpdate.push('ssn');
      updateData.ssnMasked = maskSSN(ssnFormatted);
    }

    if (updates.dateOfBirth !== undefined) {
      valuesToEncrypt.push(updates.dateOfBirth);
      fieldsToUpdate.push('dob');
      updateData.dateOfBirthMasked = maskDOB(updates.dateOfBirth, true);
    }

    // Encrypt all values in one call if needed
    if (valuesToEncrypt.length > 0) {
      const encrypted = await encryptValues(valuesToEncrypt);
      let index = 0;

      fieldsToUpdate.forEach((field) => {
        if (field === 'ssn') {
          updateData.ssnEncrypted = encrypted[index];
        } else if (field === 'dob') {
          updateData.dateOfBirthEncrypted = encrypted[index];
        }
        index++;
      });
    }

    // Shared replacement path preserves the governing authorization while a new upload is uncertain.
    if (updates.hipaaAuthorizationFile && typeof updates.hipaaAuthorizationFile !== 'string') {
      await uploadAdditionalHipaa(firmId, clientId, userId, userId, updates.hipaaAuthorizationFile, {
        type: 'general', isSigned: updates.signatureStatus === 'signed',
      });
    }

    await updateDoc(clientRef, updateData);

    // HIPAA Audit: Log client update
    await logAudit({
      firmId,
      action: 'edit_client',
      resourceType: 'client',
      resourceId: clientId,
      details: {
        fieldsUpdated: Object.keys(updateData),
      },
      success: true,
    });
  }, 'update client');
}

/**
 * Update case status
 */
export async function updateCaseStatus(
  firmId: string,
  clientId: string,
  userId: string,
  status: CaseStatus
): Promise<void> {
  return withErrorHandling(async () => {
    const clientRef = doc(db, `firms/${firmId}/clients`, clientId);
    const updateData: Record<string, unknown> = {
      caseStatus: status,
      updatedAt: getCurrentTimestamp(),
      updatedBy: userId,
    };

    if (status === 'closed') {
      updateData.caseClosedDate = getCurrentTimestamp();
    } else {
      updateData.caseClosedDate = null;
    }

    await updateDoc(clientRef, updateData);

    // Sync billing when case status changes (open or closed)
    // Do this asynchronously to avoid blocking the case status update
    if (status === 'closed' || status === 'open') {
      updateSubscriptionQuantity(firmId).catch((syncError) => {
        // Log the error but don't fail the case status update
        logger.error(`Error syncing billing after case ${status === 'closed' ? 'closure' : 're-open'}:`, syncError, {
          firmId,
          clientId,
        });
        // Don't show toast - this is a background sync operation
      });
    }
  }, 'update case status');
}

/**
 * Delete a client
 */
export async function deleteClient(firmId: string, clientId: string): Promise<void> {
  return withErrorHandling(async () => {
    // Delete HIPAA authorization from Storage
    const hipaaStoragePath = `firms/${firmId}/clients/${clientId}/hipaa-authorization.pdf`;
    const hipaaRef = ref(storage, hipaaStoragePath);

    try {
      await deleteObject(hipaaRef);
    } catch (_error: unknown) {
      logger.warn('HIPAA file may not exist or already deleted');
    }

    // Delete client document
    const clientRef = doc(db, `firms/${firmId}/clients`, clientId);
    await deleteDoc(clientRef);
  }, 'delete client');
}

/**
 * Get client statistics
 */
export async function getClientStats(firmId: string): Promise<{
  totalClients: number;
  openCases: number;
  closedCases: number;
}> {
  return withErrorHandling(async () => {
    const clientsRef = collection(db, `firms/${firmId}/clients`);
    const openQuery = query(clientsRef, where('caseStatus', '==', 'open'));
    const closedQuery = query(clientsRef, where('caseStatus', '==', 'closed'));

    // Execute count queries in parallel
    const [openSnapshot, closedSnapshot] = await Promise.all([
      getCountFromServer(openQuery),
      getCountFromServer(closedQuery)
    ]);

    const openCases = openSnapshot.data().count;
    const closedCases = closedSnapshot.data().count;

    return {
      totalClients: openCases + closedCases,
      openCases,
      closedCases,
    };
  }, 'get client statistics') as Promise<{
    totalClients: number;
    openCases: number;
    closedCases: number;
  }>;
}

/**
 * Get multiple clients by IDs
 */
export async function getClientsByIds(firmId: string, clientIds: string[]): Promise<Client[]> {
  return withErrorHandling(async () => {
    if (!clientIds.length) return [];

    // Firestore 'in' query supports max 30 values
    const CHUNK_SIZE = 30;
    const chunks = [];
    for (let i = 0; i < clientIds.length; i += CHUNK_SIZE) {
      chunks.push(clientIds.slice(i, i + CHUNK_SIZE));
    }

    const results: Client[] = [];

    // Process chunks in parallel
    const promises = chunks.map(async (chunk) => {
      const clientsRef = collection(db, `firms/${firmId}/clients`);
      const q = query(clientsRef, where('clientId', 'in', chunk));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => doc.data() as Client);
    });

    const chunkResults = await Promise.all(promises);
    chunkResults.forEach(chunk => results.push(...chunk));

    return results;
  }, 'get clients by IDs') as Promise<Client[]>;
}
